/**
 * Pure display derivations for a tipping goal — computed at RENDER from the slim
 * {@link TippingGoalSummary} primitives, never pre-baked into the view model.
 * 1:1 with the legacy floating goal pill (`formatGoalAmountCompactSmart` +
 * `progressPercentage`) and the custom-button progress bar
 * (`formatTippingGoalProgressLabel`).
 */
import { formatMoney } from '@ps/functions/utils/currency';

/**
 * Compact currency label for the goal pill's `achieved / target` line.
 *   - `>= $5000` → integer-k (`$5k`, `$25k`)
 *   - `>= $1000` → one-decimal-k (`$1.2k`, `$1.3k`)
 *   - else → plain dollars (`$500`, `$999`)
 */
export function formatTipGoalAmountCompact(value: number): string {
  if (value >= 5000) {
    return `$${Math.round(value / 1000)}k`;
  }
  if (value >= 1000) {
    const roundedHundreds = Math.round(value / 100);
    return `$${(roundedHundreds / 10).toFixed(1)}k`;
  }
  return `$${Math.round(value)}`;
}

/**
 * `"<achieved> of <target> raised"` caption for the custom tipping button's
 * inline progress bar — a 1:1 port of the legacy
 * `formatTippingGoalProgressLabel(goal, { compact: true })`: amounts `>= $1000`
 * collapse to integer-k with a `USD` suffix (`1k USD`), smaller amounts render
 * as full money (`$500.00`). Distinct from the floating goal pill, which uses
 * the `$`-prefixed {@link formatTipGoalAmountCompact} form.
 */
export function formatTipGoalProgressLabel(
  achievedAmount: number,
  targetAmount: number,
): string {
  const achieved = compactGoalAmount(Math.min(achievedAmount, targetAmount));
  const target = compactGoalAmount(targetAmount);
  return `${achieved} of ${target} raised`;
}

function compactGoalAmount(value: number): string {
  // ≥ $5k → integer-k. $1k–$4,999 → one-decimal-k, so a $3,500 target reads
  // "3.5k USD" — legacy's `formatGoalAmount({compact})` blindly `toFixed(0)`d
  // and showed "4k USD" for it (QA: "for 3.5k why it shows 4k"); this borrows
  // the precision rule from legacy's own `formatGoalAmountCompactSmart` (the
  // floating goal pill) while keeping this label's `k USD` suffix style. A
  // whole-k value drops the decimal ("1k USD", not "1.0k USD").
  if (value >= 5000) {
    return `${Math.round(value / 1000)}k USD`;
  }
  if (value >= 1000) {
    const oneDecimalK = Math.round(value / 100) / 10;
    return `${
      Number.isInteger(oneDecimalK)
        ? oneDecimalK.toFixed(0)
        : oneDecimalK.toFixed(1)
    }k USD`;
  }
  return formatMoney(value, 'USD');
}

/** Goal completion as a 0–100 percentage, clamped (0 target → 0%). */
export function tipGoalProgressPercent(
  achievedAmount: number,
  targetAmount: number,
): number {
  if (targetAmount <= 0) {
    return 0;
  }
  return Math.min(achievedAmount / targetAmount, 1) * 100;
}
